import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Link,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Close, DoneAll, Edit, VerifiedUser } from "@mui/icons-material";
import { OcrComparison, OcrService } from "../../services/ocr/OcrService";
import { AnalysisService } from "../../services/analysis/AnalysisService";
import { videoPlayerService } from "../../services/video-player/VideoPlayerService";
import { translationService } from "../../services/translation/TranslationService";
import { snackbarNotificationsService } from "../../services/notifications/SnackbarNotificationsService";
import AnalysisControls, {
  AnalysisDisplayOptions,
  defaultAnalysisDisplayOptions,
} from "./AnalysisControls";
import { mergeDetections } from "./detectionMerge";
import { classifyComparison } from "./detectionMatch";
import ComparisonMetricsPanel, { ComparisonFilter } from "./ComparisonMetricsPanel";
import DetectionEditDialog, { EditableDetection } from "./DetectionEditDialog";
import DetectionLegend, { LegendCode } from "./DetectionLegend";
import { downloadVerifiedCsv, downloadVerifiedHtml } from "./verifiedReportExport";
import { reportAssets } from "./reportAssets";
import {
  ocrTsToSeconds,
  secondsToOcrTimestamp,
  activeIndexByTime,
} from "./playbackSync";

interface IInspectionComparisonProps {
  ocrService: OcrService;
  videoAnalysis: AnalysisService;
  /**
   * Persist the reviewer's decisions to the inspection review. Called alongside the legacy
   * comparison blob so this tab's edits reach the same record the sign-off header approves —
   * without it, work done here would never appear in the review or the standards export.
   */
  onSaveReview?(
    detections: any[],
    mainInformation: Array<{ key: string; value: string }>
  ): Promise<void>;
  /**
   * Copy the rows checked in "To Add" into the VIDEO-ANALYSIS approved list.
   *
   * Checking a row already folds it into the ground truth on the left, which is the OCR side's
   * record. That says nothing about the analysis list -- the two are approved separately and
   * produce separate documents -- so accepting an AI proposal here used to leave the analysis
   * side untouched, and the reviewer had to find the same rows again on the Video Analysis tab.
   *
   * Omitted, no button is rendered: the caller decides whether an analysis list exists to add to.
   */
  onAddCheckedToApproved?(
    rows: Array<{
      mainCode: string;
      c1: string;
      c2: string;
      meters: number;
      timestamp: number;
      accuracy?: number;
    }>,
    /** Called after each row so the button can count up rather than just spin. */
    onProgress?: (done: number, total: number) => void
  ): Promise<void>;
  /**
   * Compare the two APPROVED lists instead of the raw machine output.
   *
   * Supplied by the page when the user switches the Comparison tab to
   * "approved vs approved": the OCR-side approved list becomes the ground truth and the
   * analysis-side approved list replaces the model's proposals. Both must be given together —
   * comparing an approved list against raw output would be neither of the two modes.
   */
  groundTruthOverride?: Array<Record<string, any>>;
  markersOverride?: Array<Record<string, any>>;
  /**
   * The rows already Approved in the Damage Detection Review Workbench. Only used to badge the
   * AI rows that are there already: a tick in To Add says nothing about the workbench, and
   * reviewers kept reading the ticks as "these are reviewed".
   */
  workbenchRows?: Array<{ mainCode: string; meters: number; timestamp?: number }>;
}

const DEFAULT_TOLERANCE = 0.3;
// How close an AI row must be to a workbench row to count as the same damage.
const WORKBENCH_MATCH_METRES = 0.05;
const WORKBENCH_MATCH_SECONDS = 0.5;

// Stable identity for a detection / AI marker: same main code at (roughly) the
// same metre. Used to dedupe AI proposals against ground truth and to track the
// user's add/remove choices across re-renders and tolerance changes.
const ocrKey = (mainCode: string, distance: number) =>
  `${mainCode}@${distance.toFixed(2)}`;

const formatTime = (ts: string | undefined): string => {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

// AI markers carry a video offset in seconds. Rendered as m:ss, the same clock
// format the Inspection Graph tables use, rather than raw seconds.
const formatSeconds = (totalSeconds: number): string => {
  const s = Math.max(0, Math.round(totalSeconds));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
};

const t = (key: string) => translationService.translate(key);

// The Inspector and AI tables sit either side of one pipe, with leader lines between them, so
// they must line up: same title block, single-line headers, and rows of one fixed height.
const tableTitleSx = { mb: 1, lineHeight: "32px" } as const;
//
// Cells are packed tight (8px a side instead of MUI's 16) so the AI table -- seven columns in a
// 5/12 column -- shows To Add and Dismiss without scrolling.
const comparisonTableSx = {
  "& .MuiTableCell-root": { px: 1 },
  "& .MuiTableHead-root .MuiTableCell-root": { whiteSpace: "nowrap", height: 40 },
  "& .MuiTableBody-root .MuiTableRow-root": { height: 44 },
  "& .MuiTableBody-root .MuiTableCell-root": { whiteSpace: "nowrap" },
} as const;

// Status chips sized to sit in a 44px row beside the other cells.
const statusChipSx = { height: 22, fontSize: "0.72rem", "& .MuiChip-label": { px: 0.75 } } as const;

/**
 * Why an AI row is (or is not) ticked, and whether it is in the Damage Detection workbench.
 * The tick alone cannot say either: it is set automatically for matches and by hand for
 * AI-only rows, and it is never read from the stored review.
 */
const RowStatus: React.FC<{ dismissed: boolean; matched: boolean; inWorkbench: boolean }> = ({
  dismissed,
  matched,
  inWorkbench,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 0.5 }}>
    {dismissed ? (
      <Chip size="small" variant="outlined" sx={statusChipSx} label={t("comparison.status.dismissed")} />
    ) : matched ? (
      <Tooltip title={t("comparison.status.matched-hint")}>
        <Chip size="small" variant="outlined" color="primary" sx={statusChipSx} label={t("comparison.status.matched")} />
      </Tooltip>
    ) : (
      <Tooltip title={t("comparison.status.aiOnly-hint")}>
        <Chip size="small" variant="outlined" color="warning" sx={statusChipSx} label={t("comparison.status.aiOnly")} />
      </Tooltip>
    )}
    {/* An icon, not a worded chip: the column has room for one label, and the tooltip names it. */}
    {inWorkbench && (
      <Tooltip title={`${t("comparison.status.inWorkbench")} — ${t("comparison.status.inWorkbench-hint")}`}>
        <VerifiedUser color="success" sx={{ fontSize: 18 }} aria-label={t("comparison.status.inWorkbench")} />
      </Tooltip>
    )}
  </Box>
);

// One arrow anchor on the central pipe: a coloured dot positioned by metre
// (pos / axisMax) that draws a leader line to its table row.
interface PipeMarker {
  pos: number;
  color: string;
  rowIndex: number;
}

// The shared pipe sitting between the two tables. Ground-truth markers fan their
// leader lines left (to the Inspector rows); AI markers fan right (to the AI rows).
// The dots only carry their metre position and a stable DOM id; LeaderLineOverlay
// measures those ids and draws the arrows itself.
const ComparisonPipeColumn: React.FC<{
  leftItems: PipeMarker[];
  rightItems: PipeMarker[];
  axisMax: number;
  activeLeftIndex?: number;
  activeRightIndex?: number;
}> = ({ leftItems, rightItems, axisMax, activeLeftIndex, activeRightIndex }) => {
  const pct = (pos: number) =>
    axisMax > 0 ? Math.min(100, Math.max(0, (pos / axisMax) * 100)) : 0;

  const dotSx = (it: PipeMarker, active: boolean) => ({
    position: "absolute" as const,
    left: "50%",
    top: `${pct(it.pos)}%`,
    transform: "translate(-50%, -50%)",
    width: active ? 14 : 9,
    height: active ? 14 : 9,
    borderRadius: "50%",
    backgroundColor: it.color,
    border: "1px solid #1b2a38",
    boxShadow: active ? "0 0 0 3px rgba(229,57,53,0.5)" : "none",
    zIndex: active ? 2 : 1,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Spacer matching each table's title so the pipe lines up with the rows. */}
      <Typography variant="h6" gutterBottom sx={{ visibility: "hidden" }}>
        .
      </Typography>
      <Box sx={{ position: "relative", flexGrow: 1, minHeight: 300, mx: "auto", width: 40 }}>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 0,
            transform: "translateX(-50%)",
            width: 16,
            height: "100%",
            backgroundColor: "#88aacc",
            borderRadius: 8,
          }}
        />
        {leftItems.map((it) => (
          <Box
            key={`pipe-gt-${it.rowIndex}`}
            id={`pipe-gt-${it.rowIndex}`}
            sx={dotSx(it, it.rowIndex === activeLeftIndex)}
          />
        ))}
        {rightItems.map((it) => (
          <Box
            key={`pipe-ai-${it.rowIndex}`}
            id={`pipe-ai-${it.rowIndex}`}
            sx={dotSx(it, it.rowIndex === activeRightIndex)}
          />
        ))}
      </Box>
    </Box>
  );
};

// Arrow geometry matching the Inspection Graph (PipeDiagram): a 9 x 9 head, and a
// flat stub covering ~30% of the run before the line angles off to the row.
const ARROW_LENGTH = 9;
const ARROW_THICKNESS = 9;
const ARROW_BEND_RATIO = 0.3;

// One measured pipe-dot -> table-row leader line, in coordinates relative to the
// wrapper holding both tables and the pipe.
interface LeaderLine {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  /** Which way the line runs, i.e. which side of the pipe its table is on. */
  dir: "left" | "right";
}

// Measure every dot/row pair in a single O(rows) pass. react-archer used to own this,
// but it re-measured EVERY relation on each of its ResizeObserver ticks (which fire
// repeatedly while the page settles and on each playback tick) -> O(rows^2) reflow
// that froze the Comparison tab on large files. Measuring here is keyed on a geometry
// signature and never re-run for playback: the active row is a render-time style.
const measureLeaderLines = (
  container: HTMLElement,
  leftItems: PipeMarker[],
  rightItems: PipeMarker[]
): LeaderLine[] => {
  const base = container.getBoundingClientRect();
  const lines: LeaderLine[] = [];
  const collect = (side: "gt" | "ai", items: PipeMarker[]) => {
    items.forEach((it) => {
      const dot = document.getElementById(`pipe-${side}-${it.rowIndex}`);
      const row = document.getElementById(`${side}-row-${it.rowIndex}`);
      if (!dot || !row) return;
      const d = dot.getBoundingClientRect();
      const r = row.getBoundingClientRect();
      if (!r.height) return; // filtered out, or not laid out yet
      lines.push({
        key: `${side}-${it.rowIndex}`,
        // Ground truth sits left of the pipe, so its line leaves the dot on the left
        // and lands on the row's right edge; the AI side is mirrored.
        x1: (side === "gt" ? d.left : d.right) - base.left,
        y1: d.top + d.height / 2 - base.top,
        x2: (side === "gt" ? r.right : r.left) - base.left,
        y2: r.top + r.height / 2 - base.top,
        color: it.color,
        dir: side === "gt" ? "left" : "right",
      });
    });
  };
  collect("gt", leftItems);
  collect("ai", rightItems);
  return lines;
};

// The leader lines themselves: one absolutely-positioned SVG over the whole
// table/pipe/table row. Pointer events are off so the tables stay clickable, and each
// line ends on the row edge facing the pipe -- i.e. in the gutter, not over the text.
// The row currently playing is drawn thicker so it still stands out of the fan.
const LeaderLineOverlay: React.FC<{ lines: LeaderLine[]; activeKeys: string[] }> = ({
  lines,
  activeKeys,
}) => (
  <Box
    component="svg"
    sx={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      overflow: "visible",
      zIndex: 2,
    }}
  >
    {lines.map((l) => {
      const active = activeKeys.indexOf(l.key) >= 0;
      // Same shape as the Inspection Graph's arrows (PipeDiagram): a short flat stub
      // out of the dot, then one straight diagonal to the row -- no curve.
      const sign = l.dir === "left" ? -1 : 1;
      const bendX = l.x1 + sign * Math.abs(l.x2 - l.x1) * ARROW_BEND_RATIO;
      const path = `M ${l.x1},${l.y1} L ${bendX},${l.y1} L ${l.x2},${l.y2}`;
      // Arrow head, tip on the row edge, pointing along the diagonal segment.
      const angle = Math.atan2(l.y2 - l.y1, l.x2 - bendX);
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const bx = l.x2 - ARROW_LENGTH * cos;
      const by = l.y2 - ARROW_LENGTH * sin;
      const hx = (ARROW_THICKNESS / 2) * -sin;
      const hy = (ARROW_THICKNESS / 2) * cos;
      const head = `${l.x2},${l.y2} ${bx + hx},${by + hy} ${bx - hx},${by - hy}`;
      return (
        <g key={l.key}>
          <path d={path} fill="none" stroke={l.color} strokeWidth={active ? 2 : 1.5} />
          <polygon points={head} fill={l.color} />
        </g>
      );
    })}
  </Box>
);

const InspectionComparison: React.FC<IInspectionComparisonProps> = observer(
  ({
    ocrService,
    videoAnalysis,
    onSaveReview,
    onAddCheckedToApproved,
    groundTruthOverride,
    markersOverride,
    workbenchRows,
  }) => {
    const [tolerance, setTolerance] = useState(DEFAULT_TOLERANCE);
    // Metric filter from the AI-vs-Inspector summary: clicking Matched / AI only /
    // Missed narrows BOTH tables and the pipe to that category.
    const [metricFilter, setMetricFilter] = useState<ComparisonFilter>("all");
    // Verify/edit state, keyed by a STABLE row id (not value) so edits survive
    // re-sorts and value changes: removed rows, per-row edits, user-added rows.
    const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
    const [editedById, setEditedById] = useState<Map<string, Partial<EditableDetection>>>(new Map());
    const [addedRows, setAddedRows] = useState<Array<EditableDetection & { _id: string }>>([]);
    const [editTarget, setEditTarget] = useState<{ id: string; initial: Partial<EditableDetection> } | null>(null);
    const [addOpen, setAddOpen] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
    // AI proposals the reviewer explicitly DISMISSED. Distinct from simply not being
    // checked: "looked at and rejected" is part of the audit trail, and the review keeps
    // these rows rather than dropping them.
    const [rejectedKeys, setRejectedKeys] = useState<Set<string>>(new Set());
    // The AI table is triaged lowest-confidence-first by default, so the proposals most
    // likely to need a human decision are at the top. Distance order stays available
    // because that is what lines up with the pipe diagram beside it.
    const [aiSort, setAiSort] = useState<"confidence" | "distance">("distance");
    const [saving, setSaving] = useState(false);
    const [hidden, setHidden] = useState<Set<string>>(new Set());
    const [options, setOptions] = useState<AnalysisDisplayOptions>(
      defaultAnalysisDisplayOptions
    );

    // Ground-truth baseline: the previously saved comparison if there is one,
    // otherwise the raw OCR detections.
    const baseGroundTruth = useMemo(() => {
      const saved = ocrService.comparison?.verifiedDetections;
      // An override means the user asked to compare the APPROVED lists, so the OCR-side
      // approved list is the ground truth and the saved comparison blob is not consulted.
      const source = groundTruthOverride
        ? groundTruthOverride
        : saved && saved.length
        ? saved
        : ocrService.detections;
      return source.map((d: any) => ({
        main_Code: d.main_Code ?? "",
        distance: Number(d.distance ?? 0),
        timestamp: d.timestamp ?? "",
        text: d.text ?? "",
        c1: d.c1 ?? "",
        c2: d.c2 ?? "",
        q1: d.q1 ?? "",
        q2: d.q2 ?? "",
        sa: d.sa ?? "",
        postionFrom: d.postionFrom ?? "",
        positionTo: d.positionTo ?? "",
        frameUrl: d.frameUrl ?? "",
      }));
    }, [ocrService.comparison, ocrService.detections, groundTruthOverride]);

    // AI detections built from the predictions (so we have C1/C2/accuracy for the
    // "Show Characterizations" option), refined by "Merge Near Regions". Overall
    // Confidence is applied upstream by the service (it drives allPredictions).
    const distancesTable = videoAnalysis.vaData?.distances ?? [];
    const colorOf = (videoAnalysis as any).mainCodeColors as Record<string, string>;
    const markers = useMemo(() => {
      // Approved-vs-approved mode: the analysis-side approved list stands in for the model's
      // proposals, so none of the prediction/RoI derivation below applies.
      if (markersOverride) {
        return markersOverride.map((m: any) => ({
          mainCode: m.mainCode ?? "",
          c1: m.c1 ?? "",
          c2: m.c2 ?? "",
          accuracy: Number(m.accuracy ?? 0),
          meters: Number(m.meters ?? 0),
          timestamp: Number(m.timestamp ?? 0),
          color: colorOf?.[m.mainCode] ?? "gray",
        }));
      }
      const distanceAt = (time: number): number => {
        let meters = 0;
        let best = -Infinity;
        for (const d of distancesTable) {
          if (d.time <= time && d.time > best) {
            best = d.time;
            meters = d.meters;
          }
        }
        return meters;
      };
      let list: any[];
      if (videoAnalysis.hasRoiData) {
        // Region-level RoIs (own peak distance). The RoI structure carries no
        // C1/C2, so — like the demo's get_characterizations_values_of_main_code —
        // we look up the characterizations at the RoI's peak time/main code so
        // "Show Characterizations" has something to append.
        list = videoAnalysis.roiDetections.map((r) => {
          const chars = (videoAnalysis as any).characterizationAtTime(r.time)?.[r.mainCode];
          return {
            mainCode: r.mainCode,
            c1: chars?.C1 ?? "",
            c2: chars?.C2 ?? "",
            accuracy: r.accuracy,
            meters: r.meters,
            timestamp: r.time,
            beginDistance: r.beginDistance,
            endDistance: r.endDistance,
            color: r.color,
          };
        });
      } else {
        const byKey = new Map<string, any>();
        videoAnalysis.allPredictions
          .filter((p) => p?.predictionDetails?.mainCode)
          .forEach((p) => {
            const m = {
              mainCode: p.predictionDetails.mainCode,
              c1: p.predictionDetails.c1Code || "",
              c2: p.predictionDetails.c2Code || "",
              accuracy: p.predictionDetails.accuracy ?? 0,
              meters: distanceAt(p.timestamp),
              timestamp: p.timestamp,
              color: colorOf?.[p.predictionDetails.mainCode] ?? "gray",
            };
            const key = `${m.mainCode}@${Math.round(m.timestamp)}`;
            const ex = byKey.get(key);
            if (!ex || m.accuracy > ex.accuracy) byKey.set(key, m);
          });
        list = Array.from(byKey.values());
      }

      // Ensure every ground-truth code's matching AI detection appears, even if it
      // is below the current confidence threshold — so a match always shows. For a
      // GT row with no AI detection of that code within tolerance at the active
      // threshold, pull in the AI's best (highest-accuracy) RoI for that code near
      // that distance from the full low-confidence superset.
      if (videoAnalysis.hasRoiData && baseGroundTruth.length) {
        const allRois = (videoAnalysis as any).roiDetectionsAtMinConfidence as Array<{
          mainCode: string;
          time: number;
          meters: number;
          beginDistance: number;
          endDistance: number;
          accuracy: number;
          color: string;
        }>;
        baseGroundTruth.forEach((g) => {
          const alreadyShown = list.some(
            (m) => m.mainCode === g.main_Code && Math.abs(m.meters - g.distance) <= tolerance
          );
          if (alreadyShown) return;
          const candidates = allRois.filter(
            (r) => r.mainCode === g.main_Code && Math.abs(r.meters - g.distance) <= tolerance
          );
          if (!candidates.length) return;
          const best = candidates.reduce((a, b) => (b.accuracy > a.accuracy ? b : a));
          if (list.some((m) => m.mainCode === best.mainCode && Math.abs(m.meters - best.meters) < 0.001))
            return;
          const chars = (videoAnalysis as any).characterizationAtTime(best.time)?.[best.mainCode];
          list.push({
            mainCode: best.mainCode,
            c1: chars?.C1 ?? "",
            c2: chars?.C2 ?? "",
            accuracy: best.accuracy,
            meters: best.meters,
            timestamp: best.time,
            beginDistance: best.beginDistance,
            endDistance: best.endDistance,
            color: best.color,
          });
        });
      } else if (!videoAnalysis.hasRoiData && baseGroundTruth.length) {
        // Same idea for the per-timestamp predictions path (no RoI data): for a GT
        // code with no AI detection within tolerance at the active threshold, pull in
        // the best (highest-accuracy) prediction of that code near that distance from
        // the full 0.65 superset — so a match always shows even at 100% overall.
        const allLow = ((videoAnalysis as any).predictionsForSmallestAccuracy ?? []) as Array<{
          timestamp: number;
          predictionDetails: { mainCode: string; c1Code: string; c2Code: string; accuracy: number };
        }>;
        baseGroundTruth.forEach((g) => {
          const alreadyShown = list.some(
            (m) => m.mainCode === g.main_Code && Math.abs(m.meters - g.distance) <= tolerance
          );
          if (alreadyShown) return;
          const candidates = allLow
            .filter((p) => p?.predictionDetails?.mainCode === g.main_Code)
            .map((p) => ({ p, meters: distanceAt(p.timestamp) }))
            .filter((c) => Math.abs(c.meters - g.distance) <= tolerance);
          if (!candidates.length) return;
          const best = candidates.reduce((a, b) =>
            (b.p.predictionDetails.accuracy ?? 0) > (a.p.predictionDetails.accuracy ?? 0) ? b : a
          );
          if (list.some((m) => m.mainCode === best.p.predictionDetails.mainCode && Math.abs(m.meters - best.meters) < 0.001))
            return;
          list.push({
            mainCode: best.p.predictionDetails.mainCode,
            c1: best.p.predictionDetails.c1Code || "",
            c2: best.p.predictionDetails.c2Code || "",
            accuracy: best.p.predictionDetails.accuracy ?? 0,
            meters: best.meters,
            timestamp: best.p.timestamp,
            color: colorOf?.[best.p.predictionDetails.mainCode] ?? "gray",
          });
        });
      }

      const byMetre =
        videoAnalysis.sectionLength > 0 ||
        distancesTable.length > 0 ||
        list.some((m) => m.meters > 0);
      const posOf = (m: any) => (byMetre ? m.meters : m.timestamp);
      list.sort((a, b) => posOf(a) - posOf(b));
      if (options.mergeNearRegions) {
        // Span-based de-dup: collapse same-code RoIs whose distance spans overlap
        // / sit within the threshold into one (peak accuracy wins).
        list = mergeDetections(list, { gap: options.mergeDistance, byMetre });
      }
      return list;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      markersOverride,
      videoAnalysis.allPredictions,
      videoAnalysis.hasRoiData,
      videoAnalysis.roiDetections,
      videoAnalysis.roiDetectionsAtMinConfidence,
      baseGroundTruth,
      tolerance,
      distancesTable,
      videoAnalysis.sectionLength,
      options.mergeNearRegions,
      options.mergeDistance,
    ]);

    /**
     * A detection's code as one token: BCA + E + A reads BCAEA.
     *
     * Shared by BOTH tables. The inspector side used to print the main code alone while the AI
     * side honoured the toggle, so the same observation appeared as BCA on the left and BCAEA on
     * the right -- which reads as a disagreement about the coding rather than a difference in
     * what each column chooses to show. Accepts either field spelling: the analysis rows carry
     * mainCode, the OCR rows main_Code.
     */
    const codeWithChars = (m: { mainCode?: string; main_Code?: string; c1?: string; c2?: string }) => {
      const main = m.mainCode ?? m.main_Code ?? "";
      return options.showCharacterizations ? `${main}${m.c1 ?? ""}${m.c2 ?? ""}` : main;
    };

    // Single source of truth for AI↔ground-truth matching: matched (covered),
    // aiOnly (proposed/"To Add"), inspectorOnly (missed), and per-code tp/fp/fn.
    const classification = useMemo(
      () => classifyComparison(markers, baseGroundTruth, tolerance),
      [markers, baseGroundTruth, tolerance]
    );
    // proposed ("To Add") = AI detections with no ground-truth match.
    const proposedKeys = useMemo(
      () => new Set(classification.aiOnly.map((m: any) => ocrKey(m.mainCode, m.meters))),
      [classification]
    );
    // covered = AI detections that DO match a ground-truth detection.
    const coveredKeys = useMemo(
      () => new Set(classification.matched.map((m: any) => ocrKey(m.mainCode, m.meters))),
      [classification]
    );

    // Whenever there is ground truth, auto-check the AI rows that match it (the
    // AI agrees with the inspector). Merge into the user's selections rather than
    // replace, so manual "To Add" ticks on missing detections are preserved.
    useEffect(() => {
      if (baseGroundTruth.length === 0 || coveredKeys.size === 0) return;
      setCheckedKeys((prev) => {
        const next = new Set(prev);
        coveredKeys.forEach((k) => next.add(k));
        return next;
      });
    }, [coveredKeys, baseGroundTruth.length]);

    // Proposals (proposedKeys) HIGHLIGHT the recommended (missing) AI rows; matched
    // rows are auto-checked above. Changing the confidence selector re-proposes but
    // never reshuffles the Inspector (Ground Truth) list — matched rows are not
    // re-added to it (see groundTruth below), so there are no duplicates.

    // An accepted AI marker becomes a ground-truth row. Its confidence and
    // characterizations are carried across rather than blanked: they are the
    // provenance of the AI proposal, and the ISYBAU/DWA export needs C1/C2.
    const toMarkerDetection = (m: {
      mainCode: string;
      meters: number;
      timestamp: number;
      accuracy?: number;
      c1?: string;
      c2?: string;
    }) => ({
      main_Code: m.mainCode,
      distance: m.meters,
      timestamp: secondsToOcrTimestamp(m.timestamp),
      text: "",
      c1: m.c1 ?? "",
      c2: m.c2 ?? "",
      q1: "",
      q2: "",
      sa: "",
      postionFrom: "",
      positionTo: "",
      frameUrl: "",
      confidence: m.accuracy ?? 0,
      source: "AI",
      decision: "Accepted",
    });

    // The verified ground truth shown on the left and persisted on save. Each row
    // carries a STABLE `_id`; user edits (editedById) are applied by id, removed
    // rows dropped, and user-added rows appended. Sources: inspector base rows,
    // accepted-AI rows (checked + not already covered), and manually added rows.
    const groundTruth = useMemo(() => {
      const applyEdit = <T extends { _id: string }>(r: T) => {
        return { ...r, ...editedById.get(r._id) } as any;
      };
      const base = baseGroundTruth
        .map((g, i) => ({ ...g, _id: `gt-${i}` }))
        .filter((r) => !removedIds.has(r._id))
        .map(applyEdit);
      const acceptedAi = markers
        .filter((m) => {
          const k = ocrKey(m.mainCode, m.meters);
          return checkedKeys.has(k) && !coveredKeys.has(k);
        })
        .map((m) => ({ ...toMarkerDetection(m), _id: `aiadd-${ocrKey(m.mainCode, m.meters)}` }))
        .filter((r) => !removedIds.has(r._id))
        .map(applyEdit);
      const added = addedRows.filter((r) => !removedIds.has(r._id)).map(applyEdit);
      return [...base, ...acceptedAi, ...added].sort((a, b) => a.distance - b.distance);
    }, [baseGroundTruth, removedIds, editedById, addedRows, markers, checkedKeys, coveredKeys]);

    // Pipe axis + arrow anchors. Both sides share one metre scale (section length
    // or the furthest detection) so a ground-truth row and the AI row at the same
    // metre line up across the pipe.
    const pipeAxisMax = useMemo(
      () =>
        Math.max(
          1,
          videoAnalysis.sectionLength || 0,
          ...groundTruth.map((g) => g.distance),
          ...markers.map((m) => m.meters)
        ),
      [videoAnalysis.sectionLength, groundTruth, markers]
    );

    // Per-main-code legend + filter (applied to BOTH tables, the pipe dots and the
    // arrows together, so everything stays index-aligned).
    const legendCodes: LegendCode[] = useMemo(() => {
      const seen = new Map<string, string>();
      groundTruth.forEach((g) => {
        if (g.main_Code && !seen.has(g.main_Code))
          seen.set(g.main_Code, colorOf?.[g.main_Code] ?? "gray");
      });
      markers.forEach((m) => {
        if (m.mainCode && !seen.has(m.mainCode))
          seen.set(m.mainCode, m.color ?? colorOf?.[m.mainCode] ?? "gray");
      });
      return Array.from(seen, ([mainCode, color]) => ({ mainCode, color }));
    }, [groundTruth, markers, colorOf]);
    const toggleCode = (code: string) =>
      setHidden((prev) => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
      });

    // Damage-class (group code) filter, from the analysis file's group→main-code map
    // (Crack/Root/Connection/Surface_Damage/Adhesive_Substance). Toggling a class
    // hides every main code under it across BOTH tables and the pipe.
    const groupOf = useMemo(() => {
      const map: Record<string, string> = {};
      ((videoAnalysis.codesAvailableForDetection as any[]) ?? []).forEach((g) => {
        (g.mainCodesWithSecondaryCodes ?? []).forEach((mc: any) => {
          map[mc.mainCode] = g.groupCode;
        });
      });
      return map;
    }, [videoAnalysis.codesAvailableForDetection]);
    const groupColorOf = useMemo(() => {
      const map: Record<string, string> = {};
      ((videoAnalysis.codesAvailableForDetection as any[]) ?? []).forEach((g) => {
        map[g.groupCode] = g.color;
      });
      return map;
    }, [videoAnalysis.codesAvailableForDetection]);
    const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
    const toggleGroup = (code: string) =>
      setHiddenGroups((prev) => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
      });
    const groupHidden = (mainCode: string) => {
      const g = groupOf[mainCode];
      return g ? hiddenGroups.has(g) : false;
    };
    // Only the classes actually present in this file's ground truth / AI markers.
    const presentGroupCodes: LegendCode[] = useMemo(() => {
      const seen = new Map<string, string>();
      const add = (mainCode: string) => {
        const g = groupOf[mainCode];
        if (g && !seen.has(g)) seen.set(g, groupColorOf[g] ?? "gray");
      };
      groundTruth.forEach((row) => add(row.main_Code));
      markers.forEach((m) => add(m.mainCode));
      return Array.from(seen, ([mainCode, color]) => ({ mainCode, color }));
    }, [groundTruth, markers, groupOf, groupColorOf]);

    // A ground-truth row is "matched" when some AI marker of the same code sits
    // within tolerance; otherwise it's "missed". AI rows use the covered/proposed
    // sets already computed from the classification.
    const gtIsMatched = (g: { main_Code: string; distance: number }) =>
      markers.some(
        (m) => m.mainCode === g.main_Code && Math.abs(m.meters - g.distance) <= tolerance
      );
    const passMetricGt = (g: any) => {
      switch (metricFilter) {
        case "matched": return gtIsMatched(g);
        case "missed": return !gtIsMatched(g);
        case "aiOnly": return false; // AI-only has no inspector rows
        default: return true;
      }
    };
    const passMetricAi = (m: any) => {
      const key = ocrKey(m.mainCode, m.meters);
      switch (metricFilter) {
        case "matched": return coveredKeys.has(key);
        case "aiOnly": return proposedKeys.has(key);
        case "missed": return false; // missed has no AI rows
        default: return true;
      }
    };

    const visibleGt = groundTruth.filter(
      (g) => !hidden.has(g.main_Code) && !groupHidden(g.main_Code) && passMetricGt(g)
    );
    const visibleAi = markers
      .filter((m) => !hidden.has(m.mainCode) && !groupHidden(m.mainCode) && passMetricAi(m))
      .slice()
      .sort((a, b) =>
        aiSort === "confidence"
          ? (a.accuracy ?? 1) - (b.accuracy ?? 1)
          : a.meters - b.meters
      );

    const leftItems: PipeMarker[] = visibleGt.map((g, i) => ({
      pos: g.distance,
      color: colorOf?.[g.main_Code] ?? "gray",
      rowIndex: i,
    }));
    const rightItems: PipeMarker[] = visibleAi.map((m, i) => ({
      pos: m.meters,
      color: m.color,
      rowIndex: i,
    }));

    // Rows currently "playing" (by video time). GT uses ISO timestamps, AI seconds.
    const activeGt = activeIndexByTime(
      visibleGt.map((g) => ocrTsToSeconds(g.timestamp)),
      videoPlayerService.videoProgress
    );
    const activeAi = activeIndexByTime(
      visibleAi.map((m) => m.timestamp),
      videoPlayerService.videoProgress
    );
    // Auto-scroll the active row into view (MUI Table has no apiRef; use its id).
    useEffect(() => {
      if (activeGt < 0 || !videoPlayerService.isVideoPlaying) return;
      document.getElementById(`gt-row-${activeGt}`)?.scrollIntoView({ block: "nearest" });
    }, [activeGt]);
    useEffect(() => {
      if (activeAi < 0 || !videoPlayerService.isVideoPlaying) return;
      document.getElementById(`ai-row-${activeAi}`)?.scrollIntoView({ block: "nearest" });
    }, [activeAi]);

    // Leader lines pipe -> rows, measured from the DOM after layout and refreshed
    // when the row geometry changes (legend/metric filters, AI sort, edits) or the
    // container resizes. Playback does NOT trigger a re-measure.
    const graphRef = useRef<HTMLDivElement | null>(null);
    const [leaderLines, setLeaderLines] = useState<LeaderLine[]>([]);
    const geometryKey =
      leftItems.map((it) => `${it.rowIndex}:${it.pos}:${it.color}`).join("|") +
      "#" +
      rightItems.map((it) => `${it.rowIndex}:${it.pos}:${it.color}`).join("|");
    useLayoutEffect(() => {
      const container = graphRef.current;
      if (!container) return;
      let frame = 0;
      const refresh = () => {
        window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() =>
          setLeaderLines(measureLeaderLines(container, leftItems, rightItems))
        );
      };
      refresh();
      const observer = new ResizeObserver(refresh);
      observer.observe(container);
      return () => {
        window.cancelAnimationFrame(frame);
        observer.disconnect();
      };
      // leftItems/rightItems are rebuilt on every render; geometryKey is their identity.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geometryKey]);
    const activeKeys = [
      activeGt >= 0 ? `gt-${activeGt}` : "",
      activeAi >= 0 ? `ai-${activeAi}` : "",
    ];

    // The rows the reviewer has accepted, in the shape the analysis list stores. Recomputed from
    // the same keys the checkboxes use, so what the button sends is exactly what is ticked.
    const checkedMarkers = useMemo(
      () =>
        markers
          .filter((m) => checkedKeys.has(ocrKey(m.mainCode, m.meters)))
          .map((m) => ({
            mainCode: m.mainCode,
            c1: m.c1 ?? "",
            c2: m.c2 ?? "",
            meters: m.meters,
            timestamp: m.timestamp,
            accuracy: m.accuracy,
          })),
      [markers, checkedKeys]
    );

    const [addProgress, setAddProgress] = useState<{ done: number; total: number } | null>(null);
    const addingToApproved = addProgress !== null;

    // Whether an AI row is already in the Damage Detection workbench. Same main code, and either
    // the same station or the same moment in the video -- not an exact `code@metre` key: the
    // stored row's distance is rounded and may have been corrected by the reviewer, so an exact
    // key missed rows that were plainly there.
    const isInWorkbench = (m: { mainCode: string; meters: number; timestamp: number }) =>
      (workbenchRows ?? []).some(
        (r) =>
          r.mainCode === m.mainCode &&
          (Math.abs(r.meters - m.meters) <= WORKBENCH_MATCH_METRES ||
            (r.timestamp !== undefined &&
              Math.abs(r.timestamp - m.timestamp) <= WORKBENCH_MATCH_SECONDS))
      );

    // What the ticks amount to, said in words above the table. The Workbench figure is the
    // whole workbench, not the ticked share of it: a reviewed row nobody has ticked here is
    // still reviewed, and showing 0 for it read as "nothing is in the workbench".
    const tickSummary = useMemo(() => {
      let auto = 0;
      checkedKeys.forEach((k) => {
        if (coveredKeys.has(k)) auto++;
      });
      const ticked = checkedKeys.size;
      return { ticked, auto, manual: ticked - auto, inWorkbench: workbenchRows?.length ?? 0 };
    }, [checkedKeys, coveredKeys, workbenchRows]);

    const addCheckedToApproved = async () => {
      if (!onAddCheckedToApproved || checkedMarkers.length === 0) return;
      setAddProgress({ done: 0, total: checkedMarkers.length });
      try {
        await onAddCheckedToApproved(checkedMarkers, (done, total) =>
          setAddProgress({ done, total })
        );
      } finally {
        setAddProgress(null);
      }
    };

    const toggleChecked = (key: string) => {
      setCheckedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };

    const removeRow = (id: string) => setRemovedIds((prev) => new Set(prev).add(id));

    // Known main codes for the edit/add dialog's autocomplete.
    const codeOptions = useMemo(() => Object.keys(colorOf || {}).sort(), [colorOf]);

    const openEdit = (row: any) =>
      setEditTarget({
        id: row._id,
        initial: {
          main_Code: row.main_Code,
          distance: row.distance,
          timestamp: row.timestamp,
          text: row.text,
          c1: row.c1,
          c2: row.c2,
          q1: row.q1,
          q2: row.q2,
          sa: row.sa,
          postionFrom: row.postionFrom,
          positionTo: row.positionTo,
          frameUrl: row.frameUrl,
        },
      });
    const saveEdit = (d: EditableDetection) => {
      if (!editTarget) return;
      setEditedById((prev) => new Map(prev).set(editTarget.id, d));
      setEditTarget(null);
    };
    const addDetection = (d: EditableDetection) => {
      setAddedRows((prev) => [
        ...prev,
        {
          ...d,
          timestamp: d.timestamp || secondsToOcrTimestamp(videoPlayerService.videoProgress),
          _id: `add-${Date.now()}-${prev.length}`,
        },
      ]);
      setAddOpen(false);
    };

    const seek = (seconds: number) => videoPlayerService.jumpTo(seconds);

    // Rows for the verified CSV/HTML. No condition class is merged in: condition and risk are
    // the reviewer's own entry on each detection, not a value derived from a rule table.
    const exportRows = () => groundTruth.map(({ _id, ...rest }: any) => rest);

    const toggleRejected = (key: string) => {
      setRejectedKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
      // A dismissed proposal must not also be queued for adding.
      setCheckedKeys((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    };

    /**
     * The reviewer's decisions in the shape the review endpoint wants: the accepted ground
     * truth, plus the AI proposals that were explicitly dismissed.
     */
    const buildReviewDetections = () => [
      ...groundTruth.map((row: any) => ({
        source: row.source || (row.addedByUser ? "User" : "OCR"),
        decision: row.decision || (row.addedByUser ? "Edited" : "Accepted"),
        confidence: row.confidence || undefined,
        mainCode: row.main_Code,
        c1: row.c1,
        c2: row.c2,
        q1: row.q1,
        q2: row.q2,
        sa: row.sa,
        positionFrom: row.postionFrom,
        positionTo: row.positionTo,
        distance: row.distance,
        timeSeconds: ocrTsToSeconds(row.timestamp),
      })),
      ...markers
        .filter((m: any) => rejectedKeys.has(ocrKey(m.mainCode, m.meters)))
        .map((m: any) => ({
          source: "AI",
          decision: "Rejected",
          confidence: m.accuracy,
          mainCode: m.mainCode,
          c1: m.c1 ?? "",
          c2: m.c2 ?? "",
          distance: m.meters,
          timeSeconds: m.timestamp,
        })),
    ];

    const save = async () => {
      setSaving(true);
      try {
        const payload: OcrComparison = {
          mainInformation: ocrService.mainInformation.map((m: any) => ({
            key: m.key,
            value: m.value,
          })),
          // Strip the internal _id before persisting.
          verifiedDetections: groundTruth.map(({ _id, ...rest }: any) => rest) as any,
        };
        await (ocrService as any).saveComparison(payload);
        await onSaveReview?.(buildReviewDetections(), payload.mainInformation as any);
        snackbarNotificationsService.showSuccess("comparison.saved");
      } catch {
        snackbarNotificationsService.showError("comparison.saveError");
      } finally {
        setSaving(false);
      }
    };

    const hasOcr = ocrService.ocrSuccessful || baseGroundTruth.length > 0;
    const hasAnalysis = videoAnalysis.analysisSuccessful;

    return (
      <Box sx={{ width: "100%", p: 1 }}>
        {(!hasOcr || !hasAnalysis) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("comparison.needsBoth")}
          </Alert>
        )}

        <AnalysisControls
          videoAnalysis={videoAnalysis}
          options={options}
          onChange={setOptions}
          mergeUnit={markers.some((m) => m.meters > 0) ? "m" : "s"}
        />

        {/* Filters on the left; on the right, over the AI side they act on, the action that sends
            ticked AI rows to the Damage Detection workbench and what the ticks currently amount
            to. Same 5/12 width as the AI column below, so the metric panels stay level. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Damage-class (group code) filter — Connection / Root / Adhesive_Substance /
                Crack / Surface_Damage — shown only for classes present in this file. */}
            {presentGroupCodes.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t("general.damages")}:
                </Typography>
                <DetectionLegend
                  codes={presentGroupCodes}
                  hidden={hiddenGroups}
                  onToggle={toggleGroup}
                  onShowAll={() => setHiddenGroups(new Set())}
                />
              </Box>
            )}

            <DetectionLegend
              codes={legendCodes}
              hidden={hidden}
              onToggle={toggleCode}
              onShowAll={() => setHidden(new Set())}
            />
          </Box>

          {(onAddCheckedToApproved || tickSummary.ticked > 0) && (
            <Box
              sx={{ width: { xs: "100%", md: `${(5 / 12) * 100}%` }, flexShrink: 0, mb: 1 }}
              data-html2canvas-ignore
            >
              {onAddCheckedToApproved && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                  <Tooltip title={t("comparison.addChecked.hint")}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={checkedMarkers.length === 0 || addingToApproved}
                        onClick={() => void addCheckedToApproved()}
                        startIcon={
                          addingToApproved ? <CircularProgress size={14} /> : <DoneAll />
                        }
                      >
                        {addProgress
                          ? `${addProgress.done} / ${addProgress.total}`
                          : `${t("comparison.addChecked.action")}${
                              checkedMarkers.length > 0 ? ` (${checkedMarkers.length})` : ""
                            }`}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              )}
              {tickSummary.ticked > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    whiteSpace: "nowrap",
                    overflowX: "auto",
                    gap: 0.75,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>
                    {t("comparison.summary.ticked")}: {tickSummary.ticked}
                  </Typography>
                  <Chip size="small" variant="outlined" color="primary"
                    label={`${tickSummary.auto} ${t("comparison.summary.auto")}`} />
                  <Chip size="small" variant="outlined" color="warning"
                    label={`${tickSummary.manual} ${t("comparison.summary.manual")}`} />
                  {workbenchRows && (
                    <Chip size="small" variant="outlined" color="success" icon={<VerifiedUser />}
                      label={`${tickSummary.inWorkbench} ${t("comparison.summary.inWorkbench")}`} />
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Each summary over the table it reads -- the inspector's coverage on the left, the
            AI's precision on the right -- in a row of its own with the same 5 / 2 / 5 columns as
            the graph below. Inside the graph's columns they pushed the tables down but not the
            pipe, which then ran past the Inspector table; out here, expanding one moves the
            whole graph down together. */}
        <Grid container spacing={2} sx={{ flexWrap: "nowrap" }}>
          <Grid item xs={12} md={5}>
            <ComparisonMetricsPanel
              side="inspector"
              classification={classification}
              groundTruthCount={baseGroundTruth.length}
              activeFilter={metricFilter}
              onSelectFilter={setMetricFilter}
            />
          </Grid>
          <Grid item xs={12} md={2} />
          <Grid item xs={12} md={5}>
            <ComparisonMetricsPanel
              side="ai"
              classification={classification}
              groundTruthCount={baseGroundTruth.length}
              activeFilter={metricFilter}
              onSelectFilter={setMetricFilter}
            />
          </Grid>
        </Grid>

        <Box ref={graphRef} sx={{ position: "relative" }}>
          <LeaderLineOverlay lines={leaderLines} activeKeys={activeKeys} />
          <Grid container spacing={2} sx={{ flexWrap: "nowrap" }}>
            {/* Inspector / ground truth (arrows enter from the right). minWidth 0 on both table
                columns: a flex item otherwise refuses to shrink below its table, which pushed the
                AI table's last columns out of view instead of letting it fit or scroll. */}
            <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={tableTitleSx}>
                {t("comparison.inspector")}
              </Typography>
              <TableContainer>
                <Table size="small" stickyHeader sx={comparisonTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("ocrService.inspectionInfo.time")}</TableCell>
                      <TableCell>{t("ocrService.inspectionInfo.distance")}</TableCell>
                      <TableCell>{t("ocrService.inspectionInfo.code")}</TableCell>
                      <TableCell align="center">Edit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleGt.map((d, i) => {
                      const seconds = ocrTsToSeconds(d.timestamp);
                      return (
                        <TableRow
                          key={`${ocrKey(d.main_Code, d.distance)}-${i}`}
                          hover
                          id={`gt-row-${i}`}
                          onClick={() => seek(seconds)}
                          sx={{
                            cursor: "pointer",
                            ...(i === activeGt ? { backgroundColor: "rgba(25,118,210,0.12)" } : {}),
                          }}
                        >
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={(e) => { e.stopPropagation(); seek(seconds); }}
                            >
                              {formatTime(d.timestamp)}
                            </Link>
                          </TableCell>
                          <TableCell>{d.distance.toFixed(2)}</TableCell>
                          <TableCell sx={{ color: colorOf?.[d.main_Code] ?? "inherit", fontWeight: 600 }}>
                            {codeWithChars(d)}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                            <IconButton size="small" aria-label="edit" onClick={() => openEdit(d)}>
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              aria-label="remove"
                              onClick={() => removeRow((d as any)._id)}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Central pipe: leader-line arrows to both tables */}
            <Grid item xs={12} md={2}>
              <ComparisonPipeColumn
                leftItems={leftItems}
                rightItems={rightItems}
                axisMax={pipeAxisMax}
                activeLeftIndex={activeGt}
                activeRightIndex={activeAi}
              />
            </Grid>

            {/* AI analytics / proposals (arrows enter from the left) */}
            <Grid item xs={12} md={5} sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={tableTitleSx}>
                {t("comparison.aiAnalytics")}
              </Typography>
              <TableContainer>
                <Table size="small" stickyHeader sx={comparisonTableSx}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("ocrService.inspectionInfo.time")}</TableCell>
                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => setAiSort("distance")}
                          title={t("comparison.sortHint")}
                        >
                          {t("ocrService.inspectionInfo.distance")}
                          {aiSort === "distance" ? " ↑" : ""}
                        </Link>
                      </TableCell>
                      <TableCell>{t("ocrService.inspectionInfo.code")}</TableCell>
                      {/* The model's own score. It was always computed for matching and
                          filtering but never shown, which left the reviewer no way to see
                          which proposals the AI was least sure about. */}
                      <TableCell align="right">
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => setAiSort("confidence")}
                          title={t("comparison.sortHint")}
                        >
                          {t("comparison.confidence")}
                          {aiSort === "confidence" ? " ↑" : ""}
                        </Link>
                      </TableCell>
                      <TableCell>{t("comparison.status.column")}</TableCell>
                      {/* width 1 = shrink to the label: the cell only ever holds a checkbox. The help
                          moved from an icon to a dotted underline, which costs no width. */}
                      <TableCell align="center" sx={{ width: 1 }}>
                        <Tooltip title={t("comparison.toAdd-help")}>
                          <Box
                            component="span"
                            sx={{ cursor: "help", borderBottom: 1, borderBottomStyle: "dotted", borderColor: "text.secondary" }}
                          >
                            {t("comparison.toAdd")}
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">{t("comparison.dismiss")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleAi.map((m, i) => {
                      const key = ocrKey(m.mainCode, m.meters);
                      const proposed = proposedKeys.has(key);
                      return (
                        <TableRow
                          key={`${key}-${i}`}
                          hover
                          id={`ai-row-${i}`}
                          selected={proposed}
                          onClick={() => seek(m.timestamp)}
                          sx={{
                            cursor: "pointer",
                            ...(i === activeAi ? { backgroundColor: "rgba(25,118,210,0.12)" } : {}),
                            ...(rejectedKeys.has(key)
                              ? { opacity: 0.45, textDecoration: "line-through" }
                              : {}),
                          }}
                        >
                          <TableCell>
                            <Link
                              component="button"
                              type="button"
                              underline="hover"
                              onClick={(e) => { e.stopPropagation(); seek(m.timestamp); }}
                            >
                              {formatSeconds(m.timestamp)}
                            </Link>
                          </TableCell>
                          <TableCell>{m.meters.toFixed(2)}</TableCell>
                          <TableCell sx={{ color: m.color, fontWeight: 600 }}>{codeWithChars(m)}</TableCell>
                          <TableCell align="right">
                            {m.accuracy === undefined ? "" : `${Math.round(m.accuracy * 100)}%`}
                          </TableCell>
                          <TableCell>
                            <RowStatus
                              dismissed={rejectedKeys.has(key)}
                              matched={coveredKeys.has(key)}
                              inWorkbench={isInWorkbench(m)}
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              sx={{ p: 0.5 }}
                              disabled={rejectedKeys.has(key)}
                              checked={checkedKeys.has(key)}
                              onChange={() => toggleChecked(key)}
                            />
                          </TableCell>
                          <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              color={rejectedKeys.has(key) ? "error" : "default"}
                              title={t("comparison.dismiss")}
                              onClick={() => toggleRejected(key)}
                            >
                              <Close fontSize="inherit" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>

        {/* Held while a batch runs.
            The work is a request per row, so a reviewer who kept clicking could fire a second
            batch over the first, or edit rows that are about to be overwritten by the save at
            the end. Blocking is the honest signal that the page is busy -- and it carries the
            count, so "busy" never looks like "stuck". */}
        <Backdrop
          open={addingToApproved}
          data-html2canvas-ignore
          sx={{
            position: "fixed",
            zIndex: (theme) => theme.zIndex.modal + 1,
            color: "#fff",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress
            color="inherit"
            variant={addProgress && addProgress.total > 0 ? "determinate" : "indeterminate"}
            value={
              addProgress && addProgress.total > 0
                ? (addProgress.done / addProgress.total) * 100
                : undefined
            }
          />
          <Typography variant="body2">
            {t("comparison.addChecked.working")}
            {addProgress ? `  ${addProgress.done} / ${addProgress.total}` : ""}
          </Typography>
        </Backdrop>

        {/* Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 3, flexWrap: "wrap" }}>
          <Box sx={{ minWidth: 280 }}>
            <Typography variant="body2" gutterBottom>
              {t("comparison.tolerance")}: <b>{tolerance.toFixed(2)} m</b>
            </Typography>
            <Slider
              value={tolerance}
              min={0}
              max={1}
              step={0.1}
              marks
              valueLabelDisplay="auto"
              onChange={(_e, v) => setTolerance(v as number)}
            />
          </Box>
          <Button startIcon={<Add />} variant="outlined" onClick={() => setAddOpen(true)}>
            Add detection
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              downloadVerifiedCsv(exportRows())
            }
            disabled={groundTruth.length === 0}
          >
            Export Verified (CSV)
          </Button>
          <Button
            variant="outlined"
            onClick={() =>
              downloadVerifiedHtml(
                exportRows(),
                ocrService.mainInformation.map((m: any) => ({ key: m.key, value: m.value })),
                { graphImage: reportAssets.analysisGraphImage }
              )
            }
            disabled={groundTruth.length === 0}
          >
            {t("comparison.exportHtml")}
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={saving || !hasOcr}
          >
            {t("comparison.save")}
          </Button>
        </Box>

        {/* Edit an existing verified detection */}
        <DetectionEditDialog
          open={editTarget !== null}
          title="Edit detection"
          initial={editTarget?.initial}
          codeOptions={codeOptions}
          onSave={saveEdit}
          onCancel={() => setEditTarget(null)}
        />
        {/* Add a new verified detection */}
        <DetectionEditDialog
          open={addOpen}
          title="Add detection"
          initial={null}
          codeOptions={codeOptions}
          onSave={addDetection}
          onCancel={() => setAddOpen(false)}
        />
      </Box>
    );
  }
);

export default InspectionComparison;
